import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Truck, Clock, Plus, Calendar, ChevronLeft, ChevronRight, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function TrucksPage() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    purpose: 'delivery',
    carrier: '',
    appointment_date: selectedDate.toISOString().split('T')[0],
    appointment_time: '08:00',
    linked_po_number: '',
    linked_so_number: '',
    notes: ''
  });
  const [poSoSearch, setPoSoSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [tableFilter, setTableFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState('appointment_date');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/api/warehouse/trucks`);
      setAppointments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setLoading(false);
    }
  };

  const generateConfirmationNumber = () => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `APT-${year}${month}${day}-${random}`;
  };

  const calculateEndTime = (startTime) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + 90; // Add 90 minutes
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const handleCreateAppointment = async () => {
    try {
      const confirmationNumber = generateConfirmationNumber();
      const endTime = calculateEndTime(newAppointment.appointment_time);
      
      const appointmentData = {
        confirmation_number: confirmationNumber,
        appointment_date: newAppointment.appointment_date,
        appointment_time: newAppointment.appointment_time,
        end_time: endTime,
        carrier: newAppointment.carrier,
        purpose: newAppointment.purpose,
        linked_po_number: newAppointment.linked_po_number || null,
        linked_so_number: newAppointment.linked_so_number || null,
        notes: newAppointment.notes,
        status: 'scheduled'
      };

      await axios.post(`${API}/api/warehouse/trucks/check-in`, appointmentData);
      setShowModal(false);
      setNewAppointment({
        purpose: 'delivery',
        carrier: '',
        appointment_date: selectedDate.toISOString().split('T')[0],
        appointment_time: '08:00',
        linked_po_number: '',
        linked_so_number: '',
        notes: ''
      });
      setPoSoSearch('');
      fetchAppointments();
    } catch (error) {
      console.error('Error creating appointment:', error);
    }
  };

  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  // Filter appointments for selected date
  const dateStr = selectedDate.toISOString().split('T')[0];
  const dayAppointments = appointments.filter(apt => apt.appointment_date === dateStr);

  // Sort by time
  dayAppointments.sort((a, b) => {
    return a.appointment_time.localeCompare(b.appointment_time);
  });

  // Filter and search table appointments
  let filteredAppointments = appointments;
  
  // Apply status filter
  if (tableFilter !== 'all') {
    filteredAppointments = filteredAppointments.filter(apt => apt.status === tableFilter);
  }
  
  // Apply search
  if (tableSearch) {
    const search = tableSearch.toLowerCase();
    filteredAppointments = filteredAppointments.filter(apt =>
      apt.confirmation_number?.toLowerCase().includes(search) ||
      apt.carrier?.toLowerCase().includes(search) ||
      apt.linked_po_number?.toLowerCase().includes(search) ||
      apt.linked_so_number?.toLowerCase().includes(search) ||
      apt.driver_name?.toLowerCase().includes(search) ||
      apt.license_plate?.toLowerCase().includes(search)
    );
  }
  
  // Apply sorting
  filteredAppointments.sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];
    
    if (sortColumn === 'appointment_date' || sortColumn === 'appointment_time') {
      aVal = `${a.appointment_date} ${a.appointment_time}`;
      bVal = `${b.appointment_date} ${b.appointment_time}`;
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });
  
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      checked_in: 'bg-green-100 text-green-700',
      loading: 'bg-yellow-100 text-yellow-700',
      loaded: 'bg-purple-100 text-purple-700',
      departed: 'bg-gray-100 text-gray-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/warehouse')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Truck className="w-8 h-8" />
              Truck Scheduling
            </h1>
            <p className="text-gray-600 mt-1">Manage delivery and pickup appointments</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Schedule Appointment
        </button>
      </div>

      {/* Date Navigator */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => changeDate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="text-2xl font-bold">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {dayAppointments.length} appointment{dayAppointments.length !== 1 ? 's' : ''} scheduled
            </div>
          </div>
          <button
            onClick={() => changeDate(1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Appointments Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Schedule</h2>
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading appointments...</div>
        ) : dayAppointments.length > 0 ? (
          <div className="space-y-3">
            {dayAppointments.map((apt) => (
              <button
                key={apt.truck_id}
                onClick={() => navigate(`/warehouse/trucks/${apt.truck_id}`)}
                className="w-full border rounded-lg p-4 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-semibold text-lg">
                          {apt.appointment_time} - {apt.end_time}
                        </span>
                      </div>
                      {getStatusBadge(apt.status)}
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        apt.purpose === 'delivery' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'
                      }`}>
                        {apt.purpose.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Confirmation #:</span>
                        <span className="ml-2 font-mono font-semibold">{apt.confirmation_number}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Carrier:</span>
                        <span className="ml-2 font-semibold">{apt.carrier}</span>
                      </div>
                      {apt.linked_po_number && (
                        <div>
                          <span className="text-gray-500">PO #:</span>
                          <span className="ml-2 font-semibold">{apt.linked_po_number}</span>
                        </div>
                      )}
                      {apt.linked_so_number && (
                        <div>
                          <span className="text-gray-500">SO #:</span>
                          <span className="ml-2 font-semibold">{apt.linked_so_number}</span>
                        </div>
                      )}
                      {apt.driver_name && (
                        <div>
                          <span className="text-gray-500">Driver:</span>
                          <span className="ml-2">{apt.driver_name}</span>
                        </div>
                      )}
                      {apt.license_plate && (
                        <div>
                          <span className="text-gray-500">License Plate:</span>
                          <span className="ml-2 font-mono">{apt.license_plate}</span>
                        </div>
                      )}
                      {apt.dock_door_id && (
                        <div>
                          <span className="text-gray-500">Dock Door:</span>
                          <span className="ml-2">{apt.dock_door_id}</span>
                        </div>
                      )}
                    </div>
                    
                    {apt.notes && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-semibold">Notes:</span> {apt.notes}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No Appointments Scheduled</p>
            <p className="text-sm">Click "Schedule Appointment" to add a new appointment</p>
          </div>
        )}
      </div>

      {/* All Appointments Table */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">All Appointments</h2>
        
        {/* Search and Filter */}
        <div className="mb-4 space-y-3">
          <input
            type="text"
            placeholder="Search by confirmation #, carrier, PO/SO #, driver, or license plate..."
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            {['all', 'scheduled', 'checked_in', 'loading', 'loaded', 'departed'].map(status => (
              <button
                key={status}
                onClick={() => setTableFilter(status)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  tableFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ').toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading appointments...</div>
        ) : filteredAppointments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    onClick={() => handleSort('appointment_date')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Date {sortColumn === 'appointment_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th 
                    onClick={() => handleSort('confirmation_number')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Confirmation # {sortColumn === 'confirmation_number' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    onClick={() => handleSort('carrier')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Carrier {sortColumn === 'carrier' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Purpose
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PO/SO #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    License Plate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver
                  </th>
                  <th 
                    onClick={() => handleSort('status')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    Status {sortColumn === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAppointments.map((apt) => (
                  <tr key={apt.truck_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(apt.appointment_date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {apt.appointment_time} - {apt.end_time}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                      {apt.confirmation_number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{apt.carrier}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        apt.purpose === 'delivery' ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'
                      }`}>
                        {apt.purpose.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {apt.linked_po_number || apt.linked_so_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">
                      {apt.license_plate || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {apt.driver_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(apt.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500">
            <Truck className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No Appointments Found</p>
            <p className="text-sm">Schedule your first appointment</p>
          </div>
        )}
      </div>

      {/* Schedule Appointment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-4">Schedule Appointment</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Purpose</label>
                <select
                  value={newAppointment.purpose}
                  onChange={(e) => setNewAppointment({...newAppointment, purpose: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="delivery">Delivery (Receiving)</option>
                  <option value="pickup">Pickup (Shipping)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Carrier *</label>
                <input
                  type="text"
                  value={newAppointment.carrier}
                  onChange={(e) => setNewAppointment({...newAppointment, carrier: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter carrier name"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Appointment Date *</label>
                  <input
                    type="date"
                    value={newAppointment.appointment_date}
                    onChange={(e) => setNewAppointment({...newAppointment, appointment_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Appointment Time *</label>
                  <input
                    type="time"
                    value={newAppointment.appointment_time}
                    onChange={(e) => setNewAppointment({...newAppointment, appointment_time: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Duration: 90 minutes (End time: {calculateEndTime(newAppointment.appointment_time)})
              </p>

              {newAppointment.purpose === 'delivery' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Purchase Order # *</label>
                  <input
                    type="text"
                    value={newAppointment.linked_po_number}
                    onChange={(e) => {
                      setNewAppointment({...newAppointment, linked_po_number: e.target.value});
                      setPoSoSearch(e.target.value);
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search or enter PO number (e.g., PO-12345)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Type to search existing POs or create new</p>
                </div>
              )}

              {newAppointment.purpose === 'pickup' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Sales Order # *</label>
                  <input
                    type="text"
                    value={newAppointment.linked_so_number}
                    onChange={(e) => {
                      setNewAppointment({...newAppointment, linked_so_number: e.target.value});
                      setPoSoSearch(e.target.value);
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search or enter SO number (e.g., SO-12345)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Type to search existing SOs or create new</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={newAppointment.notes}
                  onChange={(e) => setNewAppointment({...newAppointment, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Special instructions or notes"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAppointment}
                disabled={!newAppointment.carrier}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
